import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import HospitalLogo from '@medical/shared/components/HospitalLogo';
import AdminTopBar from '../components/AdminTopBar';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import { format, subDays } from 'date-fns';
import { calculateAge } from '@medical/shared/utils/ageUtils';
import { hasFeature } from '@medical/shared/utils/features';
import { translateZoneNameToEnglish } from '@medical/shared/utils/translateZoneName';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const DashboardContainer = styled.div`
  display: flex;
  min-height: 100vh;
  background-color: #f8fafc;
`;

const Sidebar = styled.div`
  width: 280px;
  background: #0F1F4D;
  color: white;
  height: 100vh;
  position: fixed;
  left: 0;
  top: 0;
  box-shadow: 2px 0 8px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  z-index: 100;
`;

const SidebarHeader = styled.div`
  padding: 2rem 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const TAB_TITLES = {
  overview: 'Overview',
  analytics: 'Analytics',
  profile: 'Hospital Profile',
  patients: 'Patients',
  doctors: 'Doctors',
  'front-desk': 'Front Desk',
  accounts: 'Accounts'
};

const NavMenu = styled.nav`
  flex: 1;
  overflow-y: auto;
  padding: 1rem 0;
`;

const NavItem = styled.button`
  width: 100%;
  padding: 0.75rem 1.5rem;
  border: none;
  background: ${props => props.active ? 'rgba(255, 255, 255, 0.2)' : 'transparent'};
  color: white;
  text-align: left;
  cursor: pointer;
  font-size: 0.9375rem;
  font-weight: ${props => props.active ? '600' : '400'};
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.75rem;

  &:hover {
    background: rgba(255, 255, 255, 0.15);
  }
`;

const NavIcon = styled.span`
  font-size: 1.125rem;
  width: 24px;
  text-align: center;
`;

const MainContent = styled.main`
  margin-left: 280px;
  min-height: 100vh;
  padding: 2rem;
  width: calc(100% - 280px);
`;

const TabContent = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  padding: 2rem;
`;

const PageTitle = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 1.5rem 0;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const StatCard = styled.div`
  background: linear-gradient(135deg, ${props => props.color || '#0B63C5'} 0%, ${props => props.color2 || '#0B63C5'} 100%);
  border-radius: 12px;
  padding: 1.5rem;
  color: white;
`;

const StatLabel = styled.div`
  font-size: 0.875rem;
  opacity: 0.9;
  margin-bottom: 0.5rem;
  font-weight: 500;
`;

const StatValue = styled.div`
  font-size: 2.5rem;
  font-weight: 700;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 1rem;
`;

const TableHeader = styled.thead`
  background: #f3f4f6;
`;

const TableRow = styled.tr`
  border-bottom: 1px solid #e5e7eb;
  
  &:hover {
    background: #f9fafb;
  }
`;

const TableHeaderCell = styled.th`
  padding: 0.75rem 1rem;
  text-align: left;
  font-weight: 600;
  font-size: 0.875rem;
  color: #374151;
`;

const TableCell = styled.td`
  padding: 0.75rem 1rem;
  font-size: 0.875rem;
  color: #1f2937;
`;

const Badge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
  background: ${props => props.variant === 'success' ? '#d1fae5' : props.variant === 'danger' ? '#fee2e2' : '#e0e7ff'};
  color: ${props => props.variant === 'success' ? '#065f46' : props.variant === 'danger' ? '#991b1b' : '#3730a3'};
`;

const Button = styled.button`
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  border: none;
  transition: all 0.2s ease;
  margin-right: 0.5rem;
  
  ${props => {
    if (props.variant === 'primary') {
      return `
        background: #0B63C5;
        color: white;
        &:hover { transform: translateY(-1px); box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4); }
      `;
    } else if (props.variant === 'danger') {
      return `
        background: #ef4444;
        color: white;
        &:hover { background: #dc2626; }
      `;
    } else if (props.variant === 'success') {
      return `
        background: #10b981;
        color: white;
        &:hover { background: #059669; }
      `;
    } else {
      return `
        background: white;
        color: #374151;
        border: 1px solid #d1d5db;
        &:hover { background: #f9fafb; }
      `;
    }
  }}
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 16px;
  padding: 2rem;
  max-width: 500px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 1.5rem 0;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  font-weight: 500;
  color: #374151;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: #0B63C5;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const PasswordField = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
`;

const PasswordInput = styled(Input)`
  padding-right: 2.75rem;
  width: 100%;
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: 0.5rem;
  top: 50%;
  transform: translateY(-50%);
  z-index: 2;
  min-width: 3.5rem;
  padding: 0.35rem 0.5rem;
  border: none;
  background: rgba(0,0,0,0.04);
  border-radius: 6px;
  color: #4b5563;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  &:hover {
    background: rgba(0,0,0,0.08);
    color: #1f2937;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 1rem;
  background: white;
  
  &:focus {
    outline: none;
    border-color: #0B63C5;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
  margin-top: 1.5rem;
`;


const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 3rem;
  color: #6b7280;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  color: #6b7280;
`;

const ChartsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(500px, 1fr));
  gap: 2rem;
  margin-bottom: 2rem;
`;

const ChartCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const ChartTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 1rem 0;
`;

const ChartContainer = styled.div`
  position: relative;
  height: 300px;
`;

const AnalyticsSection = styled.div`
  margin-bottom: 3rem;
`;

const FilterContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
  padding: 1rem;
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const FilterLabel = styled.label`
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
`;

const FilterSelect = styled.select`
  padding: 0.5rem 1rem;
  border-radius: 6px;
  border: 1px solid #d1d5db;
  font-size: 0.875rem;
  background: white;
  color: #374151;
  cursor: pointer;
  min-width: 150px;
  
  &:focus {
    outline: none;
    border-color: #0B63C5;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 1.5rem 0;
`;

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
`;

const MetricCard = styled.div`
  background: white;
  border-radius: 8px;
  padding: 1.25rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const MetricLabel = styled.div`
  font-size: 0.875rem;
  color: #6b7280;
  margin-bottom: 0.5rem;
`;

const MetricValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: #1f2937;
`;

const TableContainer = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow-x: auto;
`;

const RadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const RadioOptionRow = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.75rem;
  border: 1px solid ${props => props.selected ? '#6366f1' : '#e5e7eb'};
  border-radius: 12px;
  cursor: pointer;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  background: ${props => props.selected ? 'rgba(99, 102, 241, 0.08)' : 'white'};

  &:hover {
    border-color: #6366f1;
    box-shadow: 0 8px 16px rgba(99, 102, 241, 0.08);
  }
`;

const RadioTitle = styled.span`
  font-weight: 600;
  color: #111827;
`;

const RadioDescription = styled.span`
  display: block;
  color: #6b7280;
  font-size: 0.875rem;
  margin-top: 0.25rem;
`;

const CheckboxList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  max-height: 220px;
  overflow-y: auto;
  padding: 0.25rem;
`;

const CheckboxOption = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  cursor: pointer;
  transition: border-color 0.2s ease;

  &:hover {
    border-color: #6366f1;
  }
`;

const CheckboxText = styled.div`
  display: flex;
  flex-direction: column;
  font-size: 0.875rem;
  color: #111827;
`;

const CheckboxMeta = styled.span`
  color: #6b7280;
  font-size: 0.75rem;
`;

const ProfileHero = styled.div`
  background: #0B63C5;
  border-radius: 20px;
  padding: 2.5rem;
  color: white;
  display: flex;
  flex-wrap: wrap;
  gap: 2rem;
  margin-bottom: 2rem;
  box-shadow: 0 20px 45px rgba(76, 29, 149, 0.25);
`;

const HeroMain = styled.div`
  flex: 1 1 360px;
  max-width: 640px;
`;

const HeroName = styled.h2`
  font-size: 2.5rem;
  font-weight: 800;
  margin: 0;
`;

const HeroTagline = styled.p`
  font-size: 1.1rem;
  margin: 0.75rem 0 1.25rem 0;
  color: rgba(255, 255, 255, 0.9);
`;

const HeroMission = styled.p`
  font-size: 1rem;
  line-height: 1.6;
  margin: 0;
  color: rgba(255, 255, 255, 0.85);
`;

const HeroMeta = styled.div`
  margin-top: 1.75rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
`;

const MetaBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.45rem 0.85rem;
  border-radius: 9999px;
  background: rgba(255, 255, 255, 0.18);
  font-size: 0.85rem;
  font-weight: 600;
  letter-spacing: 0.01em;
`;

const HeroHighlights = styled.div`
  flex: 1 1 240px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 1rem;
`;

const HighlightCard = styled.div`
  background: rgba(255, 255, 255, 0.14);
  border-radius: 16px;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  min-width: 150px;
`;

const HighlightValue = styled.span`
  font-size: 1.35rem;
  font-weight: 700;
`;

const HighlightLabel = styled.span`
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.85);
`;

const DetailSection = styled.section`
  margin-bottom: 2.5rem;
`;

const DetailHeader = styled.div`
  margin-bottom: 1rem;
`;

const DetailTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 700;
  color: #111827;
  margin: 0;
`;

const DetailSubtitle = styled.p`
  margin: 0.5rem 0 0 0;
  color: #6b7280;
  font-size: 0.95rem;
`;

const DetailsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 1.5rem;
`;

const DetailCard = styled.div`
  background: #f8fafc;
  border-radius: 16px;
  border: 1px solid #e5e7eb;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const DetailLabel = styled.h3`
  font-size: 1rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
`;

const DetailText = styled.p`
  margin: 0;
  color: #4b5563;
  line-height: 1.5;
  font-size: 0.95rem;
`;

const DetailList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  color: #4b5563;
  font-size: 0.95rem;
`;

const DetailListItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;

  &:before {
    content: '•';
    color: #6366f1;
    font-weight: 700;
    line-height: 1;
  }
`;

const ContactLine = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
`;

const ContactLabel = styled.span`
  font-size: 0.8rem;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const ContactValue = styled.span`
  font-size: 1rem;
  color: #1f2937;
  font-weight: 600;
`;

const ExternalLink = styled.a`
  color: #4c1d95;
  font-weight: 600;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const TwoColumnGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 1.5rem;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const Textarea = styled.textarea`
  width: 100%;
  min-height: 120px;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 1rem;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: #0B63C5;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1.25rem;
`;

const FormHelperText = styled.p`
  margin: 0.5rem 0 0 0;
  font-size: 0.85rem;
  color: #6b7280;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: center;
`;

const formatDateForInput = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  return date.toISOString().split('T')[0];
};

const toLines = (values = []) => (Array.isArray(values) ? values.filter(Boolean).join('\n') : '');

const highlightsToLines = (highlights = []) =>
  (Array.isArray(highlights)
    ? highlights
        .map((item) => `${item.label || ''}|${item.value || ''}`)
        .join('\n')
    : '');

const leadershipToLines = (leadership = []) =>
  (Array.isArray(leadership)
    ? leadership
        .map((item) => `${item.name || ''}|${item.title || ''}|${item.phone || ''}|${item.email || ''}`)
        .join('\n')
    : '');

const splitLines = (text = '') =>
  text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

const transformProfileToForm = (profile) => ({
  name: profile?.name || '',
  tagline: profile?.tagline || '',
  mission: profile?.mission || '',
  establishedYear: profile?.establishedYear ? String(profile.establishedYear) : '',
  accreditation: toLines(profile?.accreditation),
  highlights: highlightsToLines(profile?.highlights),
  addressLine1: profile?.address?.line1 || '',
  addressLine2: profile?.address?.line2 || '',
  addressCity: profile?.address?.city || '',
  addressState: profile?.address?.state || '',
  addressPostalCode: profile?.address?.postalCode || '',
  addressCountry: profile?.address?.country || '',
  contactMainLine: profile?.contact?.mainLine || '',
  contactAppointmentCenter: profile?.contact?.appointmentCenter || '',
  contactEmail: profile?.contact?.email || '',
  emergencyHotline: profile?.emergency?.hotline || '',
  emergencyTraumaBay: profile?.emergency?.traumaBay || '',
  emergencyAmbulanceDesk: profile?.emergency?.ambulanceDesk || '',
  hoursOutpatient: profile?.hours?.outpatient || '',
  hoursEmergency: profile?.hours?.emergency || '',
  hoursPharmacy: profile?.hours?.pharmacy || '',
  hoursDiagnostics: profile?.hours?.diagnostics || '',
  specialties: toLines(profile?.specialties),
  services: toLines(profile?.services),
  leadership: leadershipToLines(profile?.leadership),
  socialWebsite: profile?.social?.website || '',
  socialLinkedin: profile?.social?.linkedin || '',
  socialFacebook: profile?.social?.facebook || '',
  socialInstagram: profile?.social?.instagram || ''
});

const convertFormToPayload = (form) => {
  const highlights = splitLines(form.highlights).map((line) => {
    const [label, value] = line.split('|');
    return {
      label: (label || '').trim(),
      value: (value || '').trim()
    };
  }).filter((item) => item.label || item.value);

  const leadership = splitLines(form.leadership).map((line) => {
    const [name, title, phone, email] = line.split('|');
    return {
      name: (name || '').trim(),
      title: (title || '').trim(),
      phone: (phone || '').trim(),
      email: (email || '').trim()
    };
  }).filter((item) => item.name);

  const toObject = (obj) =>
    Object.fromEntries(
      Object.entries(obj).map(([key, value]) => [key, value?.trim ? value.trim() : value])
    );

  const yearValue = form.establishedYear ? form.establishedYear.trim() : '';
  const parsedYear = yearValue ? Number(yearValue) : undefined;

  return {
    name: form.name.trim(),
    tagline: form.tagline.trim(),
    mission: form.mission.trim(),
    establishedYear: Number.isNaN(parsedYear) ? undefined : parsedYear,
    accreditation: splitLines(form.accreditation),
    highlights,
    address: toObject({
      line1: form.addressLine1,
      line2: form.addressLine2,
      city: form.addressCity,
      state: form.addressState,
      postalCode: form.addressPostalCode,
      country: form.addressCountry
    }),
    contact: toObject({
      mainLine: form.contactMainLine,
      appointmentCenter: form.contactAppointmentCenter,
      email: form.contactEmail
    }),
    emergency: toObject({
      hotline: form.emergencyHotline,
      traumaBay: form.emergencyTraumaBay,
      ambulanceDesk: form.emergencyAmbulanceDesk
    }),
    hours: toObject({
      outpatient: form.hoursOutpatient,
      emergency: form.hoursEmergency,
      pharmacy: form.hoursPharmacy,
      diagnostics: form.hoursDiagnostics
    }),
    specialties: splitLines(form.specialties),
    services: splitLines(form.services),
    leadership,
    social: toObject({
      website: form.socialWebsite,
      linkedin: form.socialLinkedin,
      facebook: form.socialFacebook,
      instagram: form.socialInstagram
    })
  };
};

const AdminDashboard = () => {
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [dateFilter, setDateFilter] = useState('7days'); // Default to 7 days
  const [patients, setPatients] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [frontDeskForm, setFrontDeskForm] = useState({ firstName: '', lastName: '', email: '', phone: '' });
  const [frontDeskResult, setFrontDeskResult] = useState(null);
  const [frontDeskError, setFrontDeskError] = useState('');
  const [frontDeskFieldErrors, setFrontDeskFieldErrors] = useState({});
  const [frontDeskSaving, setFrontDeskSaving] = useState(false);
  const [frontDeskStaff, setFrontDeskStaff] = useState([]);
  const [frontDeskStatusBusy, setFrontDeskStatusBusy] = useState(null);
  const [hospitalProfile, setHospitalProfile] = useState(null);
  const [profileError, setProfileError] = useState(null);
  const [profileForm, setProfileForm] = useState(null);
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState(null);
  const [showAddDoctorPassword, setShowAddDoctorPassword] = useState(false);
  const [showAddDoctorConfirmPassword, setShowAddDoctorConfirmPassword] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [selectedDoctorData, setSelectedDoctorData] = useState(null);
  const [formData, setFormData] = useState({});
  const [modalLoading, setModalLoading] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const navigate = useNavigate();

  // Helper function to calculate patient age in real-time
  const getPatientAge = (patient) => {
    if (!patient?.dateOfBirth) return patient?.age || null;
    return calculateAge(patient.dateOfBirth);
  };

  // Auto-update age when month changes
  useEffect(() => {
    const checkMonthChange = () => {
      const now = new Date();
      const currentMonthNow = now.getMonth();
      if (currentMonthNow !== currentMonth) {
        setCurrentMonth(currentMonthNow);
        // Force re-render by updating patients state
        setPatients(prev => [...prev]);
      }
    };

    // Check every hour for month change
    const interval = setInterval(checkMonthChange, 60 * 60 * 1000);
    
    // Also check on mount
    checkMonthChange();

    return () => clearInterval(interval);
  }, [currentMonth]);

  useEffect(() => {
    loadUserData();
    loadData();
  }, [activeTab, dateFilter]);

  useEffect(() => {
    if (activeTab !== 'front-desk') setFrontDeskResult(null);
  }, [activeTab]);

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) return;
    axios.get('/api/auth/me', { headers: { Authorization: `Bearer ${token}` } })
      .then((response) => {
        const fresh = response.data?.user;
        if (!fresh) return;
        const merged = { ...JSON.parse(localStorage.getItem('adminUser') || '{}'), ...fresh };
        localStorage.setItem('adminUser', JSON.stringify(merged));
        setUser(merged);
      })
      .catch(() => {});
  }, []);

  const canSeeAnalytics = hasFeature(user, 'analytics');
  useEffect(() => {
    if (!canSeeAnalytics && activeTab === 'analytics') setActiveTab('overview');
  }, [canSeeAnalytics, activeTab]);

  const loadUserData = () => {
    const userData = localStorage.getItem('adminUser');
    if (userData) {
      setUser(JSON.parse(userData));
    }
    // Set auth token
    const token = localStorage.getItem('adminToken');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'overview') {
        const statsRes = await axios.get('/api/admin/stats');
        setStats(statsRes.data.stats);
      }
      if (activeTab === 'analytics' && hasFeature(JSON.parse(localStorage.getItem('adminUser') || '{}'), 'analytics')) {
        setAnalyticsLoading(true);
        try {
          const analyticsRes = await axios.get('/api/admin/analytics', {
            params: { dateFilter }
          });
          setAnalytics(analyticsRes.data.analytics);
        } catch (error) {
          console.error('Error loading analytics:', error);
          toast.error('Failed to load analytics data');
        } finally {
          setAnalyticsLoading(false);
        }
      }
      if (activeTab === 'patients' || activeTab === 'accounts') {
        const patientsRes = await axios.get('/api/admin/patients');
        setPatients(patientsRes.data.patients);
      }
      if (activeTab === 'doctors' || activeTab === 'accounts') {
        const doctorsRes = await axios.get('/api/admin/doctors');
        setDoctors(doctorsRes.data.doctors);
      }
      if (activeTab === 'front-desk' || activeTab === 'accounts') {
        await loadFrontDeskStaff();
      }
      if (activeTab === 'profile') {
        const profileRes = await axios.get('/api/admin/profile');
        setHospitalProfile(profileRes.data.profile);
        setProfileError(null);
        if (!isEditingProfile) {
          setProfileForm(transformProfileToForm(profileRes.data.profile));
        }
      }
    } catch (error) {
      console.error('Error loading data:', error);
      if (activeTab === 'profile') {
        setHospitalProfile(null);
        if (!isEditingProfile) {
          setProfileForm(null);
        }
        setProfileError(error.response?.data?.message || 'Failed to load hospital profile');
        toast.error('Failed to load hospital profile');
      } else {
        toast.error('Failed to load data');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadFrontDeskStaff = async () => {
    const response = await axios.get('/api/admin/front-desk');
    setFrontDeskStaff(response.data.frontDeskStaff || []);
  };

  const toggleFrontDeskStatus = async (staff) => {
    const nextActive = !staff.isActive;
    if (!nextActive && !window.confirm(`Deactivate ${staff.fullName || staff.frontDeskId}? They will be signed out and cannot log in until reactivated.`)) {
      return;
    }
    setFrontDeskStatusBusy(staff._id);
    try {
      const response = await axios.patch(`/api/admin/front-desk/${staff._id}/status`, { isActive: nextActive });
      setFrontDeskStaff((prev) => prev.map((item) => (item._id === staff._id ? { ...item, isActive: nextActive } : item)));
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Could not update front desk status');
    } finally {
      setFrontDeskStatusBusy(null);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminRefreshToken');
    localStorage.removeItem('adminUser');
    delete axios.defaults.headers.common['Authorization'];
    window.dispatchEvent(new Event('adminLogout'));
    toast.success('Logged out successfully');
    navigate('/admin', { replace: true });
  };

  const handleReassignPatient = async (patientId, doctorId) => {
    try {
      await axios.put(`/api/admin/patients/${patientId}/reassign`, { doctorId });
      toast.success('Patient reassigned successfully');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to reassign patient');
    }
  };

  const handleUpdatePatient = async () => {
    if (!selectedPatient) {
      return;
    }

    const firstName = formData.firstName ? formData.firstName.trim() : '';
    const phone = formData.phone ? formData.phone.trim() : '';
    const assignedDoctor = formData.assignedDoctor ? formData.assignedDoctor : '';

    if (!firstName) {
      toast.error('First name is required');
      return;
    }

    if (!phone) {
      toast.error('Phone number is required');
      return;
    }

    if (!assignedDoctor) {
      toast.error('Please select an assigned doctor');
      return;
    }

    setModalLoading(true);

    try {
      const payload = {
        firstName,
        lastName: formData.lastName !== undefined ? formData.lastName.trim() : '',
        phone,
        isActive: formData.isActive,
        assignedDoctor,
      };

      if (formData.dateOfBirth) {
        payload.dateOfBirth = formData.dateOfBirth;
      }

      await axios.put(`/api/admin/users/${selectedPatient}`, payload);
      toast.success('Patient updated successfully');
      closeModal();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update patient');
    } finally {
      setModalLoading(false);
    }
  };


  const handleAddDoctor = async () => {
    const firstName = formData.firstName ? formData.firstName.trim() : '';
    const lastName = formData.lastName ? formData.lastName.trim() : '';
    const email = formData.email ? formData.email.trim() : '';
    const phone = formData.phone ? formData.phone.trim() : '';

    if (!firstName) {
      toast.error('First name is required');
      return;
    }

    if (!email) {
      toast.error('Email is required');
      return;
    }

    if (!phone) {
      toast.error('Phone number is required');
      return;
    }

    setModalLoading(true);

    try {
      const response = await axios.post('/api/admin/doctors', {
        firstName,
        lastName,
        email,
        phone
      });
      toast.success(`Doctor ${response.data.doctor.doctorId} created. Initial password is ${response.data.temporaryPassword}.`);
      closeModal();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to add doctor');
    } finally {
      setModalLoading(false);
    }
  };

  const handleUpdateDoctor = async () => {
    try {
      await axios.put(`/api/admin/doctors/${selectedDoctor}`, formData);
      toast.success('Doctor updated successfully');
      closeModal();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update doctor');
    }
  };

  const handleToggleActive = async (userId, payload = {}, messages = {}) => {
    try {
      await axios.put(`/api/admin/users/${userId}/toggle-active`, payload);
      toast.success(messages.success || 'User status updated');
      loadData();
      return true;
    } catch (error) {
      toast.error(error.response?.data?.message || messages.error || 'Failed to update user status');
      return false;
    }
  };

  const handleDeactivateDoctor = async () => {
    if (!selectedDoctor) {
      return;
    }

    const patientCount = Number(formData.patientCount || 0);
    const availableDoctors = doctors.filter(
      (doctor) => doctor.isActive && doctor._id !== selectedDoctor
    );

    if (patientCount > 0 && availableDoctors.length === 0) {
      toast.error('No other active doctors are available to receive patients. Activate another doctor first.');
      return;
    }

    if (formData.strategy === 'selected' && (!formData.targetDoctorIds || formData.targetDoctorIds.length === 0)) {
      toast.error('Select at least one doctor to receive reassigned patients.');
      return;
    }

    setModalLoading(true);

    const payload = {
      strategy: formData.strategy,
    };

    if (formData.strategy === 'selected') {
      payload.targetDoctorIds = formData.targetDoctorIds;
    }

    const success = await handleToggleActive(
      selectedDoctor,
      payload,
      {
        success: 'Doctor deactivated and patients reassigned successfully',
        error: 'Failed to deactivate doctor'
      }
    );

    if (success) {
      closeModal();
    } else {
      setModalLoading(false);
    }
  };

  const handleStartProfileEdit = () => {
    if (!hospitalProfile) {
      return;
    }
    setProfileForm(transformProfileToForm(hospitalProfile));
    setIsEditingProfile(true);
  };

  const handleCancelProfileEdit = () => {
    setIsEditingProfile(false);
    if (hospitalProfile) {
      setProfileForm(transformProfileToForm(hospitalProfile));
    }
    setProfileSaving(false);
  };

  const handleProfileFieldChange = (field, value) => {
    setProfileForm((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleProfileSave = async () => {
    if (!profileForm) {
      return;
    }

    setProfileSaving(true);

    try {
      const payload = convertFormToPayload(profileForm);
      const response = await axios.put('/api/admin/profile', payload);
      setHospitalProfile(response.data.profile);
      setProfileForm(transformProfileToForm(response.data.profile));
      setIsEditingProfile(false);
      setProfileError(null);
      toast.success(response.data.message || 'Hospital profile updated successfully');
    } catch (error) {
      console.error('Update hospital profile error:', error);
      toast.error(error.response?.data?.message || 'Failed to update hospital profile');
    } finally {
      setProfileSaving(false);
    }
  };

  const toggleTargetDoctorSelection = (doctorId) => {
    setFormData((prev) => {
      const current = new Set(prev.targetDoctorIds || []);
      if (current.has(doctorId)) {
        current.delete(doctorId);
      } else {
        current.add(doctorId);
      }

      return {
        ...prev,
        targetDoctorIds: Array.from(current)
      };
    });
  };

  const closeModal = () => {
    setShowModal(false);
    setModalType(null);
    setFormData({});
    setSelectedPatient(null);
    setSelectedDoctor(null);
    setSelectedDoctorData(null);
    setModalLoading(false);
    setShowAddDoctorPassword(false);
    setShowAddDoctorConfirmPassword(false);
  };

  const openModal = (type, data = null) => {
    setModalType(type);
    setModalLoading(false);
    setSelectedDoctorData(null);

    if (type === 'reassign' && data) {
      setSelectedPatient(data._id);
      const assignedDoctorId = data.assignedDoctor?._id ? data.assignedDoctor._id.toString() : '';
      setFormData({ doctorId: assignedDoctorId, assignedDoctor: assignedDoctorId });
    } else if (type === 'edit-doctor' && data) {
      setSelectedDoctor(data._id);
      setSelectedDoctorData(data);
      setFormData({
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        phone: data.phone,
        isActive: data.isActive
      });
    } else if (type === 'deactivate-doctor' && data) {
      setSelectedDoctor(data._id);
      setSelectedDoctorData(data);
      setFormData({
        strategy: 'equal',
        targetDoctorIds: [],
        patientCount: data.patientCount || 0
      });
    } else if (type === 'edit-patient' && data) {
      setSelectedPatient(data._id);
      setFormData({
        firstName: data.firstName || '',
        lastName: data.lastName || '',
        phone: data.phone || '',
        dateOfBirth: formatDateForInput(data.dateOfBirth),
        assignedDoctor: data.assignedDoctor?._id?.toString() || '',
        isActive: data.isActive ?? true,
        patientId: data.patientId || ''
      });
    } else if (type === 'add-doctor') {
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        password: '',
        confirmPassword: ''
      });
      setSelectedDoctor(null);
      setSelectedPatient(null);
    } else {
      setFormData({});
      if (type !== 'edit-doctor') {
        setSelectedDoctor(null);
      }
      if (type !== 'reassign' && type !== 'edit-patient') {
        setSelectedPatient(null);
      }
    }

    setShowModal(true);
  };

  const renderOverview = () => {
    if (loading) return <LoadingSpinner>Loading statistics...</LoadingSpinner>;
    if (!stats) return <EmptyState>No statistics available</EmptyState>;

    return (
      <>
        <PageTitle>Overview & Statistics</PageTitle>
        <StatsGrid>
          <StatCard color="#0B63C5" color2="#0B63C5">
            <StatLabel>Total Patients</StatLabel>
            <StatValue>{stats.totalPatients}</StatValue>
          </StatCard>
          <StatCard color="#10b981" color2="#059669">
            <StatLabel>Active Patients</StatLabel>
            <StatValue>{stats.activePatients}</StatValue>
          </StatCard>
          <StatCard color="#f59e0b" color2="#d97706">
            <StatLabel>Total Doctors</StatLabel>
            <StatValue>{stats.totalDoctors}</StatValue>
          </StatCard>
          <StatCard color="#ef4444" color2="#dc2626">
            <StatLabel>Inactive Accounts</StatLabel>
            <StatValue>{stats.inactivePatients + stats.inactiveDoctors}</StatValue>
          </StatCard>
          <StatCard color="#0B63C5" color2="#0F1F4D">
            <StatLabel>Total Pain Logs</StatLabel>
            <StatValue>{stats.totalPainLogs}</StatValue>
          </StatCard>
          <StatCard color="#06b6d4" color2="#0891b2">
            <StatLabel>Recent Logs (7 days)</StatLabel>
            <StatValue>{stats.recentLogs}</StatValue>
          </StatCard>
        </StatsGrid>
      </>
    );
  };

  const renderAnalytics = () => {
    if (analyticsLoading) return <LoadingSpinner>Loading analytics...</LoadingSpinner>;
    if (!analytics) return <EmptyState>No analytics data available</EmptyState>;

    return (
      <>
        <FilterContainer>
          <FilterLabel>Date Range:</FilterLabel>
          <FilterSelect 
            value={dateFilter} 
            onChange={(e) => setDateFilter(e.target.value)}
          >
            <option value="today">Today</option>
            <option value="yesterday">Yesterday</option>
            <option value="7days">Last 7 Days</option>
            <option value="30days">Last 30 Days</option>
            <option value="90days">Last 90 Days</option>
            <option value="all">All Time</option>
          </FilterSelect>
        </FilterContainer>
        {renderAnalyticsContent()}
      </>
    );
  };

  const renderAnalyticsContent = () => {

    const chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'top',
        },
        tooltip: {
          mode: 'index',
          intersect: false,
        }
      },
      scales: {
        y: {
          beginAtZero: true
        }
      }
    };

    // 1. Active Patients Chart
    const activePatientsDailyData = {
      labels: analytics.activePatients.daily.map(d => format(new Date(d.date), 'MMM dd')),
      datasets: [{
        label: 'Daily Active Patients',
        data: analytics.activePatients.daily.map(d => d.count),
        borderColor: 'rgb(102, 126, 234)',
        backgroundColor: 'rgba(102, 126, 234, 0.1)',
        tension: 0.4
      }]
    };

    // 2. Pain Log Entries by Level
    const painLogEntriesData = {
      labels: ['No Pain', 'Mild', 'Moderate', 'Severe', 'Worst Pain'],
      datasets: [{
        label: 'Pain Log Entries',
        data: [
          analytics.painLogEntries.byLevel['no-pain'],
          analytics.painLogEntries.byLevel['mild'],
          analytics.painLogEntries.byLevel['moderate'],
          analytics.painLogEntries.byLevel['severe'],
          analytics.painLogEntries.byLevel['worst-pain']
        ],
        backgroundColor: [
          'rgba(16, 185, 129, 0.8)',
          'rgba(96, 165, 250, 0.8)',
          'rgba(245, 158, 11, 0.8)',
          'rgba(249, 115, 22, 0.8)',
          'rgba(239, 68, 68, 0.8)'
        ],
        borderColor: [
          'rgb(16, 185, 129)',
          'rgb(96, 165, 250)',
          'rgb(245, 158, 11)',
          'rgb(249, 115, 22)',
          'rgb(239, 68, 68)'
        ],
        borderWidth: 1
      }]
    };

    // 3. Distress Score Trends
    const distressTrendsData = {
      labels: analytics.distressScoreTrends.daily.map(d => format(new Date(d.date), 'MMM dd')),
      datasets: [{
        label: 'Average Distress Score',
        data: analytics.distressScoreTrends.daily.map(d => d.avgScore),
        borderColor: 'rgb(239, 68, 68)',
        backgroundColor: 'rgba(239, 68, 68, 0.1)',
        tension: 0.4
      }]
    };

    // 4. Top Pain Zones
    const topPainZonesData = {
      labels: analytics.topPainZones.map(z => translateZoneNameToEnglish(z.zoneName)),
      datasets: [{
        label: 'Pain Log Count',
        data: analytics.topPainZones.map(z => z.count),
        backgroundColor: 'rgba(102, 126, 234, 0.8)',
        borderColor: 'rgb(102, 126, 234)',
        borderWidth: 1
      }]
    };

    // 5. Doctor Activity - Patients Assigned
    const doctorPatientsData = {
      labels: analytics.doctorActivity.patientsAssigned.slice(0, 10).map(d => d.name),
      datasets: [{
        label: 'Patients Assigned',
        data: analytics.doctorActivity.patientsAssigned.slice(0, 10).map(d => d.patientCount),
        backgroundColor: 'rgba(16, 185, 129, 0.8)',
        borderColor: 'rgb(16, 185, 129)',
        borderWidth: 1
      }]
    };

    // 6. Doctor Activity - Messages Sent
    const doctorMessagesData = {
      labels: analytics.doctorActivity.messagesSent.slice(0, 10).map(d => d.name),
      datasets: [{
        label: 'Messages Sent',
        data: analytics.doctorActivity.messagesSent.slice(0, 10).map(d => d.messageCount),
        backgroundColor: 'rgba(139, 92, 246, 0.8)',
        borderColor: 'rgb(139, 92, 246)',
        borderWidth: 1
      }]
    };

    // 7. Retention Rates
    const retentionData = {
      labels: ['Day 1', 'Day 7', 'Day 30'],
      datasets: [{
        label: 'Retention Rate (%)',
        data: [
          analytics.retentionRates.d1,
          analytics.retentionRates.d7,
          analytics.retentionRates.d30
        ],
        backgroundColor: [
          'rgba(102, 126, 234, 0.8)',
          'rgba(139, 92, 246, 0.8)',
          'rgba(236, 72, 153, 0.8)'
        ],
        borderColor: [
          'rgb(102, 126, 234)',
          'rgb(139, 92, 246)',
          'rgb(236, 72, 153)'
        ],
        borderWidth: 1
      }]
    };

    // 8. Patient Time Duration Distribution
    const patientDurationData = analytics.patientTimeDuration ? {
      labels: [
        '< 1 Week',
        '1-4 Weeks',
        '1-3 Months',
        '3-6 Months',
        '6-12 Months',
        '> 12 Months'
      ],
      datasets: [{
        label: 'Number of Patients',
        data: [
          analytics.patientTimeDuration.distribution.lessThanWeek,
          analytics.patientTimeDuration.distribution.oneToFourWeeks,
          analytics.patientTimeDuration.distribution.oneToThreeMonths,
          analytics.patientTimeDuration.distribution.threeToSixMonths,
          analytics.patientTimeDuration.distribution.sixToTwelveMonths,
          analytics.patientTimeDuration.distribution.moreThanTwelveMonths
        ],
        backgroundColor: [
          'rgba(59, 130, 246, 0.8)',
          'rgba(16, 185, 129, 0.8)',
          'rgba(245, 158, 11, 0.8)',
          'rgba(239, 68, 68, 0.8)',
          'rgba(139, 92, 246, 0.8)',
          'rgba(236, 72, 153, 0.8)'
        ],
        borderColor: [
          'rgb(59, 130, 246)',
          'rgb(16, 185, 129)',
          'rgb(245, 158, 11)',
          'rgb(239, 68, 68)',
          'rgb(139, 92, 246)',
          'rgb(236, 72, 153)'
        ],
        borderWidth: 1
      }]
    } : null;

    return (
      <>
        <PageTitle style={{ marginBottom: '0.5rem' }}>User Engagement Analytics</PageTitle>
        <FormHelperText style={{ margin: '0 0 1.5rem' }}>
          Showing data for {user?.hospitalName || 'your hospital'}{user?.hospitalPublicId ? ` (Hospital ID ${user.hospitalPublicId})` : ''} only.
        </FormHelperText>

        {/* Key Metrics */}
        <MetricsGrid>
          <MetricCard>
            <MetricLabel>Daily Active Patients</MetricLabel>
            <MetricValue>{analytics.activePatients.current.daily}</MetricValue>
          </MetricCard>
          <MetricCard>
            <MetricLabel>Weekly Active Patients</MetricLabel>
            <MetricValue>{analytics.activePatients.current.weekly}</MetricValue>
          </MetricCard>
          <MetricCard>
            <MetricLabel>Monthly Active Patients</MetricLabel>
            <MetricValue>{analytics.activePatients.current.monthly}</MetricValue>
          </MetricCard>
          <MetricCard>
            <MetricLabel>Total Pain Logs</MetricLabel>
            <MetricValue>{analytics.painLogEntries.total}</MetricValue>
          </MetricCard>
          <MetricCard>
            <MetricLabel>Body Diagram Usage</MetricLabel>
            <MetricValue>{analytics.featureAdoption.bodyDiagram}</MetricValue>
          </MetricCard>
          <MetricCard>
            <MetricLabel>Distress Thermometer Usage</MetricLabel>
            <MetricValue>{analytics.featureAdoption.distressThermometer}</MetricValue>
          </MetricCard>
          <MetricCard>
            <MetricLabel>Avg. Patient Time in App</MetricLabel>
            <MetricValue>{analytics.patientTimeDuration?.averageFormatted || 'N/A'}</MetricValue>
            <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
              ({analytics.patientTimeDuration?.averageDays?.toFixed(1) || 0} days)
            </div>
          </MetricCard>
          {analytics.loginSessions && (
            <>
              <MetricCard>
                <MetricLabel>Total Login Sessions</MetricLabel>
                <MetricValue>{analytics.loginSessions.totalSessions}</MetricValue>
                <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                  {analytics.loginSessions.activeSessions} active
                </div>
              </MetricCard>
              <MetricCard>
                <MetricLabel>Avg. Session Duration</MetricLabel>
                <MetricValue>{analytics.loginSessions.averageSessionDurationFormatted || 'N/A'}</MetricValue>
                <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                  {analytics.loginSessions.averageSessionDurationMinutes || 0} minutes
                </div>
              </MetricCard>
            </>
          )}
        </MetricsGrid>

        {/* Active Patients Chart */}
        <AnalyticsSection>
          <SectionTitle>Active Patients</SectionTitle>
          <ChartCard>
            <ChartTitle>Daily Active Patients (Last 30 Days)</ChartTitle>
            <ChartContainer>
              <Line data={activePatientsDailyData} options={chartOptions} />
            </ChartContainer>
          </ChartCard>
        </AnalyticsSection>

        {/* Pain Log Entries */}
        <AnalyticsSection>
          <SectionTitle>Pain Log Entries</SectionTitle>
          <ChartsGrid>
            <ChartCard>
              <ChartTitle>Pain Log Entries by Pain Level</ChartTitle>
              <ChartContainer>
                <Doughnut data={painLogEntriesData} options={chartOptions} />
              </ChartContainer>
            </ChartCard>
            <ChartCard>
              <ChartTitle>Top Pain Zones</ChartTitle>
              <ChartContainer>
                <Bar data={topPainZonesData} options={chartOptions} />
              </ChartContainer>
            </ChartCard>
          </ChartsGrid>
        </AnalyticsSection>

        {/* Distress Score Trends */}
        <AnalyticsSection>
          <SectionTitle>Distress Score Trends</SectionTitle>
          <ChartCard>
            <ChartTitle>Average Distress Score (Last 30 Days)</ChartTitle>
            <ChartContainer>
              <Line data={distressTrendsData} options={chartOptions} />
            </ChartContainer>
          </ChartCard>
        </AnalyticsSection>

        {/* Doctor Activity */}
        <AnalyticsSection>
          <SectionTitle>Doctor Activity</SectionTitle>
          <ChartsGrid>
            <ChartCard>
              <ChartTitle>Top Doctors by Patients Assigned</ChartTitle>
              <ChartContainer>
                <Bar data={doctorPatientsData} options={chartOptions} />
              </ChartContainer>
            </ChartCard>
            <ChartCard>
              <ChartTitle>Top Doctors by Messages Sent</ChartTitle>
              <ChartContainer>
                <Bar data={doctorMessagesData} options={chartOptions} />
              </ChartContainer>
            </ChartCard>
          </ChartsGrid>
        </AnalyticsSection>

        {/* Retention Rates */}
        <AnalyticsSection>
          <SectionTitle>Retention Rates</SectionTitle>
          <ChartCard>
            <ChartTitle>User Retention (D1/D7/D30)</ChartTitle>
            <ChartContainer>
              <Bar data={retentionData} options={chartOptions} />
            </ChartContainer>
            <div style={{ marginTop: '1rem', display: 'flex', gap: '2rem', justifyContent: 'center' }}>
              <div>
                <MetricLabel>Day 1 Retention</MetricLabel>
                <MetricValue style={{ fontSize: '1.5rem' }}>{analytics.retentionRates.d1}%</MetricValue>
                <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                  {analytics.retentionRates.d1Count} / {analytics.retentionRates.d1Total} users
                </div>
              </div>
              <div>
                <MetricLabel>Day 7 Retention</MetricLabel>
                <MetricValue style={{ fontSize: '1.5rem' }}>{analytics.retentionRates.d7}%</MetricValue>
                <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                  {analytics.retentionRates.d7Count} / {analytics.retentionRates.d7Total} users
                </div>
              </div>
              <div>
                <MetricLabel>Day 30 Retention</MetricLabel>
                <MetricValue style={{ fontSize: '1.5rem' }}>{analytics.retentionRates.d30}%</MetricValue>
                <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                  {analytics.retentionRates.d30Count} / {analytics.retentionRates.d30Total} users
                </div>
              </div>
            </div>
          </ChartCard>
        </AnalyticsSection>

        {/* Patient Time Duration */}
        {analytics.patientTimeDuration && (
          <AnalyticsSection>
            <SectionTitle>Patient Time Duration in Application</SectionTitle>
            <ChartsGrid>
              <ChartCard>
                <ChartTitle>Time Duration Distribution</ChartTitle>
                <ChartContainer>
                  {patientDurationData && (
                    <Bar data={patientDurationData} options={chartOptions} />
                  )}
                </ChartContainer>
                <div style={{ marginTop: '1rem', padding: '1rem', background: '#f9fafb', borderRadius: '8px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <MetricLabel>Average Time in App</MetricLabel>
                    <MetricValue style={{ fontSize: '1.5rem' }}>
                      {analytics.patientTimeDuration.averageFormatted}
                    </MetricValue>
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                    Based on {analytics.patientTimeDuration.totalPatients} active patients
                  </div>
                </div>
              </ChartCard>
              <ChartCard>
                <ChartTitle>Duration Breakdown</ChartTitle>
                <div style={{ padding: '1rem' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>&lt; 1 Week</span>
                      <MetricValue style={{ fontSize: '1.125rem' }}>
                        {analytics.patientTimeDuration.distribution.lessThanWeek}
                      </MetricValue>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>1-4 Weeks</span>
                      <MetricValue style={{ fontSize: '1.125rem' }}>
                        {analytics.patientTimeDuration.distribution.oneToFourWeeks}
                      </MetricValue>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>1-3 Months</span>
                      <MetricValue style={{ fontSize: '1.125rem' }}>
                        {analytics.patientTimeDuration.distribution.oneToThreeMonths}
                      </MetricValue>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>3-6 Months</span>
                      <MetricValue style={{ fontSize: '1.125rem' }}>
                        {analytics.patientTimeDuration.distribution.threeToSixMonths}
                      </MetricValue>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>6-12 Months</span>
                      <MetricValue style={{ fontSize: '1.125rem' }}>
                        {analytics.patientTimeDuration.distribution.sixToTwelveMonths}
                      </MetricValue>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>&gt; 12 Months</span>
                      <MetricValue style={{ fontSize: '1.125rem' }}>
                        {analytics.patientTimeDuration.distribution.moreThanTwelveMonths}
                      </MetricValue>
                    </div>
                  </div>
                </div>
              </ChartCard>
            </ChartsGrid>
          </AnalyticsSection>
        )}

        {/* Login Sessions & Time Spent */}
        {analytics.loginSessions && (
          <AnalyticsSection>
            <SectionTitle>Login Sessions & Time Spent</SectionTitle>
            <ChartsGrid>
              <ChartCard>
                <ChartTitle>Daily Login Count (Last 30 Days)</ChartTitle>
                <ChartContainer>
                  <Line 
                    data={{
                      labels: analytics.loginSessions.dailyLogins.map(d => format(new Date(d.date), 'MMM dd')),
                      datasets: [{
                        label: 'Daily Logins',
                        data: analytics.loginSessions.dailyLogins.map(d => d.count),
                        borderColor: 'rgb(59, 130, 246)',
                        backgroundColor: 'rgba(59, 130, 246, 0.1)',
                        tension: 0.4
                      }]
                    }} 
                    options={chartOptions} 
                  />
                </ChartContainer>
              </ChartCard>
              <ChartCard>
                <ChartTitle>Session Duration Distribution</ChartTitle>
                <ChartContainer>
                  <Doughnut 
                    data={{
                      labels: ['< 5 min', '5-15 min', '15-30 min', '30-60 min', '> 60 min'],
                      datasets: [{
                        label: 'Sessions',
                        data: [
                          analytics.loginSessions.durationDistribution.lessThan5Min,
                          analytics.loginSessions.durationDistribution.fiveTo15Min,
                          analytics.loginSessions.durationDistribution.fifteenTo30Min,
                          analytics.loginSessions.durationDistribution.thirtyTo60Min,
                          analytics.loginSessions.durationDistribution.moreThan60Min
                        ],
                        backgroundColor: [
                          'rgba(239, 68, 68, 0.8)',
                          'rgba(245, 158, 11, 0.8)',
                          'rgba(59, 130, 246, 0.8)',
                          'rgba(16, 185, 129, 0.8)',
                          'rgba(139, 92, 246, 0.8)'
                        ],
                        borderColor: [
                          'rgb(239, 68, 68)',
                          'rgb(245, 158, 11)',
                          'rgb(59, 130, 246)',
                          'rgb(16, 185, 129)',
                          'rgb(139, 92, 246)'
                        ],
                        borderWidth: 1
                      }]
                    }} 
                    options={chartOptions} 
                  />
                </ChartContainer>
              </ChartCard>
            </ChartsGrid>
            <ChartCard style={{ marginTop: '1.5rem' }}>
              <ChartTitle>Recent Login Sessions (Last 30 Days)</ChartTitle>
              <div style={{ padding: '1rem', maxHeight: '400px', overflowY: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #e5e7eb', background: '#f9fafb' }}>
                      <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: 600, color: '#374151' }}>Patient ID</th>
                      <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: 600, color: '#374151' }}>Patient Name</th>
                      <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: 600, color: '#374151' }}>Login Time</th>
                      <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: 600, color: '#374151' }}>Logout Time</th>
                      <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: 600, color: '#374151' }}>Duration</th>
                      <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: 600, color: '#374151' }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analytics.loginSessions.recentSessions.slice(0, 50).map((session, idx) => (
                      <tr key={idx} style={{ borderBottom: '1px solid #e5e7eb' }}>
                        <td style={{ padding: '0.75rem', fontSize: '0.875rem', color: '#1f2937' }}>{session.patientId}</td>
                        <td style={{ padding: '0.75rem', fontSize: '0.875rem', color: '#1f2937' }}>{session.patientName}</td>
                        <td style={{ padding: '0.75rem', fontSize: '0.875rem', color: '#6b7280' }}>
                          {format(new Date(session.loginTime), 'MMM dd, yyyy HH:mm')}
                        </td>
                        <td style={{ padding: '0.75rem', fontSize: '0.875rem', color: '#6b7280' }}>
                          {session.logoutTime ? format(new Date(session.logoutTime), 'MMM dd, yyyy HH:mm') : '-'}
                        </td>
                        <td style={{ padding: '0.75rem', fontSize: '0.875rem', color: session.isActive ? '#059669' : '#1f2937', fontWeight: 500 }}>
                          {session.durationFormatted || (session.isActive ? 'Active' : '-')}
                        </td>
                        <td style={{ padding: '0.75rem' }}>
                          <span style={{
                            padding: '0.25rem 0.75rem',
                            borderRadius: '12px',
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            background: session.isActive ? '#dcfce7' : '#e5e7eb',
                            color: session.isActive ? '#059669' : '#6b7280'
                          }}>
                            {session.isActive ? 'Active' : 'Completed'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {analytics.loginSessions.recentSessions.length === 0 && (
                  <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
                    No login sessions found
                  </div>
                )}
              </div>
            </ChartCard>
          </AnalyticsSection>
        )}

        {/* Feature Adoption */}
        <AnalyticsSection>
          <SectionTitle>Feature Adoption</SectionTitle>
          <ChartsGrid>
            <ChartCard>
              <ChartTitle>Feature Usage</ChartTitle>
              <div style={{ padding: '1rem' }}>
                <div style={{ marginBottom: '1rem' }}>
                  <MetricLabel>Body Diagram Usage</MetricLabel>
                  <MetricValue>{analytics.featureAdoption.bodyDiagram}</MetricValue>
                  <div style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.5rem' }}>
                    {analytics.featureAdoption.totalPatients > 0 
                      ? Math.round((analytics.featureAdoption.bodyDiagram / analytics.featureAdoption.totalPatients) * 100) 
                      : 0}% of active patients
                  </div>
                </div>
                <div>
                  <MetricLabel>Distress Thermometer Usage</MetricLabel>
                  <MetricValue>{analytics.featureAdoption.distressThermometer}</MetricValue>
                  <div style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.5rem' }}>
                    {analytics.featureAdoption.totalPatients > 0 
                      ? Math.round((analytics.featureAdoption.distressThermometer / analytics.featureAdoption.totalPatients) * 100) 
                      : 0}% of active patients
                  </div>
                </div>
              </div>
            </ChartCard>
            <ChartCard>
              <ChartTitle>Session Duration & Engagement</ChartTitle>
              <div style={{ padding: '1rem' }}>
                <MetricLabel>Average Engagement</MetricLabel>
                <MetricValue>{analytics.sessionDuration.average}</MetricValue>
                <div style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.5rem' }}>
                  Logs per day per patient
                </div>
              </div>
            </ChartCard>
          </ChartsGrid>
        </AnalyticsSection>
      </>
    );
  };

  const renderPatients = () => {
    if (loading) return <LoadingSpinner>Loading patients...</LoadingSpinner>;

    return (
      <>
        <PageTitle>Patient Management</PageTitle>
        <TabContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHeaderCell>Patient ID</TableHeaderCell>
                <TableHeaderCell>Hospital ID</TableHeaderCell>
                <TableHeaderCell>Name</TableHeaderCell>
                <TableHeaderCell>Age</TableHeaderCell>
                <TableHeaderCell>Phone</TableHeaderCell>
                <TableHeaderCell>Assigned Doctor</TableHeaderCell>
                <TableHeaderCell>Status</TableHeaderCell>
                <TableHeaderCell>Actions</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <tbody>
              {patients.map((patient) => (
                <TableRow key={patient._id}>
                  <TableCell>{patient.patientId}</TableCell>
                  <TableCell>{patient.hospitalId || '—'}</TableCell>
                  <TableCell>{patient.fullName}</TableCell>
                  <TableCell>{getPatientAge(patient) !== null ? getPatientAge(patient) : 'N/A'}</TableCell>
                  <TableCell>{patient.phone}</TableCell>
                  <TableCell>
                    {patient.assignedDoctor ? (
                      `${patient.assignedDoctor.doctorId} - ${patient.assignedDoctor.firstName} ${patient.assignedDoctor.lastName}`
                    ) : (
                      'Unassigned'
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant={patient.isActive ? 'success' : 'danger'}>
                      {patient.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Button variant="primary" onClick={() => openModal('edit-patient', patient)}>
                      Edit
                    </Button>
                    <Button onClick={() => openModal('reassign', patient)}>
                      Reassign
                    </Button>
                    <Button onClick={() => handleToggleActive(patient._id)}>
                      {patient.isActive ? 'Deactivate' : 'Activate'}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </tbody>
          </Table>
        </TabContent>
      </>
    );
  };

  const renderDoctors = () => {
    if (loading) return <LoadingSpinner>Loading doctors...</LoadingSpinner>;

    return (
      <>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <PageTitle>Doctor Management</PageTitle>
          <Button variant="primary" onClick={() => openModal('add-doctor')}>
            Add New Doctor
          </Button>
        </div>
        <TabContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHeaderCell>Doctor ID</TableHeaderCell>
                <TableHeaderCell>Name</TableHeaderCell>
                <TableHeaderCell>Email</TableHeaderCell>
                <TableHeaderCell>Phone</TableHeaderCell>
                <TableHeaderCell>Patients</TableHeaderCell>
                <TableHeaderCell>Status</TableHeaderCell>
                <TableHeaderCell>Actions</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <tbody>
              {doctors.map((doctor) => (
                <TableRow key={doctor._id}>
                  <TableCell>{doctor.doctorId}</TableCell>
                  <TableCell>{doctor.fullName}</TableCell>
                  <TableCell>{doctor.email}</TableCell>
                  <TableCell>{doctor.phone}</TableCell>
                  <TableCell>
                    <Badge variant="primary">{doctor.patientCount || 0}</Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant={doctor.isActive ? 'success' : 'danger'}>
                      {doctor.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Button variant="primary" onClick={() => openModal('edit-doctor', doctor)}>
                      Edit
                    </Button>
                    {doctor.isActive ? (
                      <Button variant="danger" onClick={() => openModal('deactivate-doctor', doctor)}>
                        Deactivate
                      </Button>
                    ) : (
                      <Button
                        variant="success"
                        onClick={async () => {
                          await handleToggleActive(doctor._id, {}, { success: 'Doctor activated successfully' });
                        }}
                      >
                        Activate
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </tbody>
          </Table>
        </TabContent>
      </>
    );
  };


  const renderAccounts = () => {
    if (loading) return <LoadingSpinner>Loading accounts...</LoadingSpinner>;

    const inactivePatients = patients.filter(p => !p.isActive);
    const inactiveDoctors = doctors.filter(d => !d.isActive);

    return (
      <>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <PageTitle>Account Management</PageTitle>
        </div>

        <TabContent>
          <h2 style={{ marginTop: 0, marginBottom: '1.5rem', fontSize: '1.5rem', fontWeight: 600 }}>Inactive Patients</h2>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHeaderCell>Patient ID</TableHeaderCell>
                <TableHeaderCell>Hospital ID</TableHeaderCell>
                <TableHeaderCell>Name</TableHeaderCell>
                <TableHeaderCell>Phone</TableHeaderCell>
                <TableHeaderCell>Actions</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <tbody>
              {inactivePatients.length === 0 ? (
                <TableRow>
                  <TableCell colSpan="5" style={{ textAlign: 'center', color: '#6b7280' }}>
                    No inactive patients
                  </TableCell>
                </TableRow>
              ) : (
                inactivePatients.map((patient) => (
                  <TableRow key={patient._id}>
                    <TableCell>{patient.patientId}</TableCell>
                    <TableCell>{patient.hospitalId || '—'}</TableCell>
                    <TableCell>{patient.fullName}</TableCell>
                    <TableCell>{patient.phone}</TableCell>
                    <TableCell>
                      <Button variant="success" onClick={() => handleToggleActive(patient._id)}>
                        Activate
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </tbody>
          </Table>

          <h2 style={{ marginTop: '2rem', marginBottom: '1.5rem', fontSize: '1.5rem', fontWeight: 600 }}>Inactive Doctors</h2>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHeaderCell>Doctor ID</TableHeaderCell>
                <TableHeaderCell>Name</TableHeaderCell>
                <TableHeaderCell>Email</TableHeaderCell>
                <TableHeaderCell>Actions</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <tbody>
              {inactiveDoctors.length === 0 ? (
                <TableRow>
                  <TableCell colSpan="4" style={{ textAlign: 'center', color: '#6b7280' }}>
                    No inactive doctors
                  </TableCell>
                </TableRow>
              ) : (
                inactiveDoctors.map((doctor) => (
                  <TableRow key={doctor._id}>
                    <TableCell>{doctor.doctorId}</TableCell>
                    <TableCell>{doctor.fullName}</TableCell>
                    <TableCell>{doctor.email}</TableCell>
                    <TableCell>
                      <Button variant="success" onClick={() => handleToggleActive(doctor._id)}>
                        Activate
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </tbody>
          </Table>

          <h2 style={{ marginTop: '2rem', marginBottom: '0.5rem', fontSize: '1.5rem', fontWeight: 600 }}>Front Desk Accounts</h2>
          <FormHelperText style={{ marginBottom: '1rem' }}>
            {frontDeskStaff.filter((staff) => staff.isActive).length} active · {frontDeskStaff.filter((staff) => !staff.isActive).length} inactive
          </FormHelperText>
          <TableContainer>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHeaderCell>Front Desk ID</TableHeaderCell>
                  <TableHeaderCell>Name</TableHeaderCell>
                  <TableHeaderCell>Email</TableHeaderCell>
                  <TableHeaderCell>Phone</TableHeaderCell>
                  <TableHeaderCell>Status</TableHeaderCell>
                  <TableHeaderCell>Actions</TableHeaderCell>
                </TableRow>
              </TableHeader>
              <tbody>
                {frontDeskStaff.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan="6" style={{ textAlign: 'center', color: '#6b7280' }}>
                      No front desk accounts
                    </TableCell>
                  </TableRow>
                ) : (
                  frontDeskStaff.map((staff) => (
                    <TableRow key={staff._id}>
                      <TableCell style={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{staff.frontDeskId || '—'}</TableCell>
                      <TableCell>{staff.fullName || '—'}</TableCell>
                      <TableCell>{staff.email || '—'}</TableCell>
                      <TableCell>{staff.phone || '—'}</TableCell>
                      <TableCell>
                        <Badge variant={staff.isActive ? 'success' : 'danger'}>{staff.isActive ? 'Active' : 'Inactive'}</Badge>
                      </TableCell>
                      <TableCell>
                        <Button
                          type="button"
                          variant={staff.isActive ? 'danger' : 'success'}
                          disabled={frontDeskStatusBusy === staff._id}
                          onClick={() => toggleFrontDeskStatus(staff)}
                        >
                          {frontDeskStatusBusy === staff._id ? 'Saving...' : staff.isActive ? 'Deactivate' : 'Activate'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </tbody>
            </Table>
          </TableContainer>
        </TabContent>
      </>
    );
  };

  const renderProfileDetails = () => {
    if (!hospitalProfile) {
      return null;
    }

    const {
      name,
      tagline,
      mission,
      establishedYear,
      accreditation = [],
      highlights = [],
      address = {},
      contact = {},
      emergency = {},
      hours = {},
      specialties = [],
      services = [],
      leadership = [],
      social = {}
    } = hospitalProfile;

    return (
      <>
        <PageTitle>Hospital Profile</PageTitle>
        <ProfileHero>
          <HeroMain>
            <HeroName>{name}</HeroName>
            {tagline && <HeroTagline>{tagline}</HeroTagline>}
            {mission && <HeroMission>{mission}</HeroMission>}
            <HeroMeta>
              {establishedYear && <MetaBadge>Established {establishedYear}</MetaBadge>}
              {accreditation.length > 0 && (
                <MetaBadge>{accreditation.length} Accreditations</MetaBadge>
              )}
            </HeroMeta>
          </HeroMain>
          {highlights.length > 0 && (
            <HeroHighlights>
              {highlights.map((item, index) => (
                <HighlightCard key={`${item.label}-${index}`}>
                  <HighlightValue>{item.value}</HighlightValue>
                  <HighlightLabel>{item.label}</HighlightLabel>
                </HighlightCard>
              ))}
            </HeroHighlights>
          )}
        </ProfileHero>

        <TabContent>
          <DetailSection>
            <DetailHeader>
              <DetailTitle>Contact & Location</DetailTitle>
              <DetailSubtitle>Connect with operational teams or locate the facility quickly.</DetailSubtitle>
            </DetailHeader>
            <DetailsGrid>
              <DetailCard>
                <DetailLabel>Campus Address</DetailLabel>
                <DetailText>
                  {[address.line1, address.line2].filter(Boolean).join(', ')}
                </DetailText>
                <DetailText>
                  {[address.city, address.state, address.postalCode].filter(Boolean).join(', ')}
                </DetailText>
                {address.country && <DetailText>{address.country}</DetailText>}
              </DetailCard>
              <DetailCard>
                <DetailLabel>Key Contacts</DetailLabel>
                <ContactLine>
                  <ContactLabel>Main Line</ContactLabel>
                  <ContactValue>{contact.mainLine || '—'}</ContactValue>
                </ContactLine>
                <ContactLine>
                  <ContactLabel>Appointment Center</ContactLabel>
                  <ContactValue>{contact.appointmentCenter || '—'}</ContactValue>
                </ContactLine>
                <ContactLine>
                  <ContactLabel>Email</ContactLabel>
                  <ContactValue>{contact.email || '—'}</ContactValue>
                </ContactLine>
              </DetailCard>
              <DetailCard>
                <DetailLabel>Emergency Response</DetailLabel>
                <ContactLine>
                  <ContactLabel>Hotline</ContactLabel>
                  <ContactValue>{emergency.hotline || '—'}</ContactValue>
                </ContactLine>
                <ContactLine>
                  <ContactLabel>Ambulance Desk</ContactLabel>
                  <ContactValue>{emergency.ambulanceDesk || '—'}</ContactValue>
                </ContactLine>
                <ContactLine>
                  <ContactLabel>Trauma Bay</ContactLabel>
                  <ContactValue>{emergency.traumaBay || '—'}</ContactValue>
                </ContactLine>
              </DetailCard>
            </DetailsGrid>
          </DetailSection>

          <DetailSection>
            <DetailHeader>
              <DetailTitle>Access & Hours</DetailTitle>
              <DetailSubtitle>Availability across critical patient touchpoints.</DetailSubtitle>
            </DetailHeader>
            <DetailsGrid>
              <DetailCard>
                <DetailLabel>Outpatient Clinics</DetailLabel>
                <DetailText>{hours.outpatient || '—'}</DetailText>
              </DetailCard>
              <DetailCard>
                <DetailLabel>Emergency & Critical Care</DetailLabel>
                <DetailText>{hours.emergency || '—'}</DetailText>
              </DetailCard>
              <DetailCard>
                <DetailLabel>Pharmacy</DetailLabel>
                <DetailText>{hours.pharmacy || '—'}</DetailText>
              </DetailCard>
              <DetailCard>
                <DetailLabel>Diagnostics & Imaging</DetailLabel>
                <DetailText>{hours.diagnostics || '—'}</DetailText>
              </DetailCard>
            </DetailsGrid>
          </DetailSection>

          <DetailSection>
            <DetailHeader>
              <DetailTitle>Clinical Focus & Services</DetailTitle>
              <DetailSubtitle>Core specialities and differentiated care programs.</DetailSubtitle>
            </DetailHeader>
            <TwoColumnGrid>
              <DetailCard>
                <DetailLabel>Centers of Excellence</DetailLabel>
                <DetailList>
                  {specialties.map((item, index) => (
                    <DetailListItem key={`specialty-${index}`}>{item}</DetailListItem>
                  ))}
                </DetailList>
              </DetailCard>
              <DetailCard>
                <DetailLabel>Signature Services</DetailLabel>
                <DetailList>
                  {services.map((item, index) => (
                    <DetailListItem key={`service-${index}`}>{item}</DetailListItem>
                  ))}
                </DetailList>
              </DetailCard>
            </TwoColumnGrid>
          </DetailSection>

          <DetailSection>
            <DetailHeader>
              <DetailTitle>Leadership & Coordination</DetailTitle>
              <DetailSubtitle>Dedicated escalation points for medical, operational, and experience teams.</DetailSubtitle>
            </DetailHeader>
            <DetailsGrid>
              {leadership.map((leader, index) => (
                <DetailCard key={`leader-${index}`}>
                  <DetailLabel>{leader.name}</DetailLabel>
                  <DetailText>{leader.title}</DetailText>
                  <ContactLine>
                    <ContactLabel>Phone</ContactLabel>
                    <ContactValue>{leader.phone || '—'}</ContactValue>
                  </ContactLine>
                  <ContactLine>
                    <ContactLabel>Email</ContactLabel>
                    <ContactValue>{leader.email || '—'}</ContactValue>
                  </ContactLine>
                </DetailCard>
              ))}
            </DetailsGrid>
          </DetailSection>

          <DetailSection>
            <DetailHeader>
              <DetailTitle>Digital Presence</DetailTitle>
              <DetailSubtitle>Share official channels with partners and patient families.</DetailSubtitle>
            </DetailHeader>
            <DetailsGrid>
              <DetailCard>
                <DetailLabel>Online Channels</DetailLabel>
                <DetailList>
                  {social.website && (
                    <DetailListItem>
                      <ExternalLink href={social.website} target="_blank" rel="noopener noreferrer">
                        Website
                      </ExternalLink>
                    </DetailListItem>
                  )}
                  {social.linkedin && (
                    <DetailListItem>
                      <ExternalLink href={social.linkedin} target="_blank" rel="noopener noreferrer">
                        LinkedIn
                      </ExternalLink>
                    </DetailListItem>
                  )}
                  {social.facebook && (
                    <DetailListItem>
                      <ExternalLink href={social.facebook} target="_blank" rel="noopener noreferrer">
                        Facebook
                      </ExternalLink>
                    </DetailListItem>
                  )}
                  {social.instagram && (
                    <DetailListItem>
                      <ExternalLink href={social.instagram} target="_blank" rel="noopener noreferrer">
                        Instagram
                      </ExternalLink>
                    </DetailListItem>
                  )}
                </DetailList>
              </DetailCard>
            </DetailsGrid>
          </DetailSection>
        </TabContent>
      </>
    );
  };

  const renderProfileForm = () => {
    if (!profileForm) {
      return null;
    }

    return (
      <TabContent>
        <DetailSection>
          <DetailHeader>
            <DetailTitle>General Information</DetailTitle>
            <DetailSubtitle>Update the primary identity and mission statement.</DetailSubtitle>
          </DetailHeader>
          <FormGrid>
            <FormGroup>
              <Label>Hospital Name *</Label>
              <Input
                value={profileForm.name}
                onChange={(e) => handleProfileFieldChange('name', e.target.value)}
                placeholder="Serenity Wellness Hospital"
                required
              />
            </FormGroup>
            <FormGroup>
              <Label>Tagline</Label>
              <Input
                value={profileForm.tagline}
                onChange={(e) => handleProfileFieldChange('tagline', e.target.value)}
                placeholder="Compassionate Care • Advanced Healing • Always On"
              />
            </FormGroup>
            <FormGroup>
              <Label>Established Year</Label>
              <Input
                value={profileForm.establishedYear}
                onChange={(e) => handleProfileFieldChange('establishedYear', e.target.value)}
                placeholder="2008"
              />
            </FormGroup>
          </FormGrid>
          <FormGroup>
            <Label>Mission Statement</Label>
            <Textarea
              value={profileForm.mission}
              onChange={(e) => handleProfileFieldChange('mission', e.target.value)}
              placeholder="Deliver integrated oncology, pain management, and wellness services..."
            />
          </FormGroup>
        </DetailSection>

        <DetailSection>
          <DetailHeader>
            <DetailTitle>Address & Contact</DetailTitle>
            <DetailSubtitle>Keep location and communication channels current.</DetailSubtitle>
          </DetailHeader>
          <FormGrid>
            <FormGroup>
              <Label>Address Line 1</Label>
              <Input
                value={profileForm.addressLine1}
                onChange={(e) => handleProfileFieldChange('addressLine1', e.target.value)}
                placeholder="123 Health Avenue"
              />
            </FormGroup>
            <FormGroup>
              <Label>Address Line 2</Label>
              <Input
                value={profileForm.addressLine2}
                onChange={(e) => handleProfileFieldChange('addressLine2', e.target.value)}
                placeholder="Skyline Medical District"
              />
            </FormGroup>
            <FormGroup>
              <Label>City</Label>
              <Input
                value={profileForm.addressCity}
                onChange={(e) => handleProfileFieldChange('addressCity', e.target.value)}
                placeholder="Hyderabad"
              />
            </FormGroup>
            <FormGroup>
              <Label>State</Label>
              <Input
                value={profileForm.addressState}
                onChange={(e) => handleProfileFieldChange('addressState', e.target.value)}
                placeholder="Telangana"
              />
            </FormGroup>
            <FormGroup>
              <Label>Postal Code</Label>
              <Input
                value={profileForm.addressPostalCode}
                onChange={(e) => handleProfileFieldChange('addressPostalCode', e.target.value)}
                placeholder="500081"
              />
            </FormGroup>
            <FormGroup>
              <Label>Country</Label>
              <Input
                value={profileForm.addressCountry}
                onChange={(e) => handleProfileFieldChange('addressCountry', e.target.value)}
                placeholder="India"
              />
            </FormGroup>
          </FormGrid>
          <FormGrid>
            <FormGroup>
              <Label>Main Line</Label>
              <Input
                value={profileForm.contactMainLine}
                onChange={(e) => handleProfileFieldChange('contactMainLine', e.target.value)}
                placeholder="+91 40 1234 5678"
              />
            </FormGroup>
            <FormGroup>
              <Label>Appointment Center</Label>
              <Input
                value={profileForm.contactAppointmentCenter}
                onChange={(e) => handleProfileFieldChange('contactAppointmentCenter', e.target.value)}
                placeholder="+91 40 5555 0101"
              />
            </FormGroup>
            <FormGroup>
              <Label>Email</Label>
              <Input
                value={profileForm.contactEmail}
                onChange={(e) => handleProfileFieldChange('contactEmail', e.target.value)}
                placeholder="care@serenitywellness.in"
              />
            </FormGroup>
          </FormGrid>
        </DetailSection>

        <DetailSection>
          <DetailHeader>
            <DetailTitle>Emergency & Hours</DetailTitle>
            <DetailSubtitle>Ensure critical response details are accurate.</DetailSubtitle>
          </DetailHeader>
          <FormGrid>
            <FormGroup>
              <Label>Emergency Hotline</Label>
              <Input
                value={profileForm.emergencyHotline}
                onChange={(e) => handleProfileFieldChange('emergencyHotline', e.target.value)}
                placeholder="+91 90000 91100"
              />
            </FormGroup>
            <FormGroup>
              <Label>Ambulance Desk</Label>
              <Input
                value={profileForm.emergencyAmbulanceDesk}
                onChange={(e) => handleProfileFieldChange('emergencyAmbulanceDesk', e.target.value)}
                placeholder="+91 90000 92200"
              />
            </FormGroup>
            <FormGroup>
              <Label>Trauma Bay</Label>
              <Input
                value={profileForm.emergencyTraumaBay}
                onChange={(e) => handleProfileFieldChange('emergencyTraumaBay', e.target.value)}
                placeholder="Emergency Bay 3, Ground Floor"
              />
            </FormGroup>
          </FormGrid>
          <FormGrid>
            <FormGroup>
              <Label>Outpatient Hours</Label>
              <Input
                value={profileForm.hoursOutpatient}
                onChange={(e) => handleProfileFieldChange('hoursOutpatient', e.target.value)}
                placeholder="Mon – Sat: 08:00 AM – 08:00 PM"
              />
            </FormGroup>
            <FormGroup>
              <Label>Emergency Hours</Label>
              <Input
                value={profileForm.hoursEmergency}
                onChange={(e) => handleProfileFieldChange('hoursEmergency', e.target.value)}
                placeholder="24/7 Emergency & Critical Care"
              />
            </FormGroup>
            <FormGroup>
              <Label>Pharmacy Hours</Label>
              <Input
                value={profileForm.hoursPharmacy}
                onChange={(e) => handleProfileFieldChange('hoursPharmacy', e.target.value)}
                placeholder="24/7 Inpatient & Outpatient Pharmacy"
              />
            </FormGroup>
            <FormGroup>
              <Label>Diagnostics Hours</Label>
              <Input
                value={profileForm.hoursDiagnostics}
                onChange={(e) => handleProfileFieldChange('hoursDiagnostics', e.target.value)}
                placeholder="Mon – Sat: 06:00 AM – 09:00 PM"
              />
            </FormGroup>
          </FormGrid>
        </DetailSection>

        <DetailSection>
          <DetailHeader>
            <DetailTitle>Specialties & Services</DetailTitle>
            <DetailSubtitle>Enter one item per line.</DetailSubtitle>
          </DetailHeader>
          <FormGrid>
            <FormGroup>
              <Label>Specialties</Label>
              <Textarea
                value={profileForm.specialties}
                onChange={(e) => handleProfileFieldChange('specialties', e.target.value)}
                placeholder="Oncology & Cancer Rehabilitation"
              />
              <FormHelperText>Enter one specialty per line.</FormHelperText>
            </FormGroup>
            <FormGroup>
              <Label>Services</Label>
              <Textarea
                value={profileForm.services}
                onChange={(e) => handleProfileFieldChange('services', e.target.value)}
                placeholder="Digital patient experience with remote monitoring"
              />
              <FormHelperText>Enter one service per line.</FormHelperText>
            </FormGroup>
          </FormGrid>
        </DetailSection>

        <DetailSection>
          <DetailHeader>
            <DetailTitle>Accreditations & Highlights</DetailTitle>
            <DetailSubtitle>Separate label and value with a pipe character (Label|Value).</DetailSubtitle>
          </DetailHeader>
          <FormGrid>
            <FormGroup>
              <Label>Accreditations</Label>
              <Textarea
                value={profileForm.accreditation}
                onChange={(e) => handleProfileFieldChange('accreditation', e.target.value)}
                placeholder="NABH Level 1 Comprehensive Cancer Care"
              />
              <FormHelperText>Enter one accreditation per line.</FormHelperText>
            </FormGroup>
            <FormGroup>
              <Label>Highlights</Label>
              <Textarea
                value={profileForm.highlights}
                onChange={(e) => handleProfileFieldChange('highlights', e.target.value)}
                placeholder="Bed Capacity|250 beds"
              />
              <FormHelperText>Format each line as Label|Value (e.g., Bed Capacity|250 beds).</FormHelperText>
            </FormGroup>
          </FormGrid>
        </DetailSection>

        <DetailSection>
          <DetailHeader>
            <DetailTitle>Leadership & Digital Presence</DetailTitle>
            <DetailSubtitle>Keep leadership contacts current and link official channels.</DetailSubtitle>
          </DetailHeader>
          <FormGrid>
            <FormGroup>
              <Label>Leadership</Label>
              <Textarea
                value={profileForm.leadership}
                onChange={(e) => handleProfileFieldChange('leadership', e.target.value)}
                placeholder="Dr. Aisha Narayanan|Chief Medical Officer|+91 40 1234 5680|a.narayanan@serenitywellness.in"
              />
              <FormHelperText>
                Format each line as Name|Title|Phone|Email. Phone and email are optional.
              </FormHelperText>
            </FormGroup>
            <FormGroup>
              <Label>Website</Label>
              <Input
                value={profileForm.socialWebsite}
                onChange={(e) => handleProfileFieldChange('socialWebsite', e.target.value)}
                placeholder="https://serenitywellness.in"
              />
            </FormGroup>
            <FormGroup>
              <Label>LinkedIn</Label>
              <Input
                value={profileForm.socialLinkedin}
                onChange={(e) => handleProfileFieldChange('socialLinkedin', e.target.value)}
                placeholder="https://www.linkedin.com/company/serenity-wellness-hospital"
              />
            </FormGroup>
            <FormGroup>
              <Label>Facebook</Label>
              <Input
                value={profileForm.socialFacebook}
                onChange={(e) => handleProfileFieldChange('socialFacebook', e.target.value)}
                placeholder="https://www.facebook.com/serenitywellnesshospital"
              />
            </FormGroup>
            <FormGroup>
              <Label>Instagram</Label>
              <Input
                value={profileForm.socialInstagram}
                onChange={(e) => handleProfileFieldChange('socialInstagram', e.target.value)}
                placeholder="https://www.instagram.com/serenitywellnesshospital"
              />
            </FormGroup>
          </FormGrid>
        </DetailSection>
      </TabContent>
    );
  };

  const renderProfile = () => {
    if (loading && !isEditingProfile) return <LoadingSpinner>Loading hospital profile...</LoadingSpinner>;
    if (profileError && !isEditingProfile) return <EmptyState>{profileError}</EmptyState>;
    if (!hospitalProfile && !isEditingProfile) {
      return <EmptyState>Hospital profile information is not available yet.</EmptyState>;
    }

    return (
      <>
        <SectionHeader>
          <PageTitle>Hospital Profile</PageTitle>
          {isEditingProfile ? (
            <ButtonRow>
              <Button onClick={handleCancelProfileEdit} disabled={profileSaving}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleProfileSave}
                disabled={profileSaving || !profileForm?.name.trim()}
              >
                {profileSaving ? 'Saving...' : 'Save Changes'}
              </Button>
            </ButtonRow>
          ) : (
            <Button variant="primary" onClick={handleStartProfileEdit}>
              Edit Profile
            </Button>
          )}
        </SectionHeader>
        {isEditingProfile ? renderProfileForm() : renderProfileDetails()}
      </>
    );
  };

  const renderModal = () => {
    if (!showModal) return null;

    return (
      <Modal onClick={closeModal}>
        <ModalContent onClick={(e) => e.stopPropagation()}>
          {modalType === 'reassign' && (
            <>
              <ModalTitle>Reassign Patient</ModalTitle>
              <FormGroup>
                <Label>Select Doctor</Label>
                <Select
                  value={formData.doctorId || ''}
                  onChange={(e) => setFormData({ ...formData, doctorId: e.target.value })}
                >
                  <option value="">Select a doctor...</option>
                  {doctors.map((doctor) => (
                    <option
                      key={doctor._id}
                      value={doctor._id?.toString()}
                      disabled={!doctor.isActive && doctor._id?.toString() !== formData.assignedDoctor}
                    >
                      {doctor.doctorId} - {doctor.fullName}{doctor.isActive ? '' : ' (Inactive)'}
                    </option>
                  ))}
                </Select>
              </FormGroup>
              <ButtonGroup>
                <Button onClick={closeModal}>Cancel</Button>
                <Button
                  variant="primary"
                  onClick={async () => {
                    await handleReassignPatient(selectedPatient, formData.doctorId);
                    closeModal();
                  }}
                  disabled={!formData.doctorId}
                >
                  Reassign
                </Button>
              </ButtonGroup>
            </>
          )}

          {modalType === 'edit-patient' && (
            <>
              <ModalTitle>Edit Patient Profile</ModalTitle>
              <FormGroup>
                <Label>Patient ID</Label>
                <Input value={formData.patientId || ''} disabled />
              </FormGroup>
              <FormGroup>
                <Label>First Name *</Label>
                <Input
                  value={formData.firstName || ''}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                />
              </FormGroup>
              <FormGroup>
                <Label>Last Name</Label>
                <Input
                  value={formData.lastName || ''}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                />
              </FormGroup>
              <FormGroup>
                <Label>Phone *</Label>
                <Input
                  value={formData.phone || ''}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
              </FormGroup>
              <FormGroup>
                <Label>Date of Birth</Label>
                <Input
                  type="date"
                  value={formData.dateOfBirth || ''}
                  onChange={(e) => setFormData({ ...formData, dateOfBirth: e.target.value })}
                />
              </FormGroup>
              <FormGroup>
                <Label>Assigned Doctor *</Label>
                <Select
                  value={formData.assignedDoctor || ''}
                  onChange={(e) => setFormData({ ...formData, assignedDoctor: e.target.value })}
                >
                  <option value="">Select a doctor...</option>
                  {doctors.map((doctor) => (
                    <option
                      key={doctor._id}
                      value={doctor._id?.toString()}
                      disabled={!doctor.isActive && doctor._id?.toString() !== formData.assignedDoctor}
                    >
                      {doctor.doctorId} - {doctor.fullName}{doctor.isActive ? '' : ' (Inactive)'}
                    </option>
                  ))}
                </Select>
              </FormGroup>
              <FormGroup>
                <Label>Status</Label>
                <Select
                  value={formData.isActive ? 'active' : 'inactive'}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.value === 'active' })}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </Select>
              </FormGroup>
              <ButtonGroup>
                <Button onClick={closeModal} disabled={modalLoading}>Cancel</Button>
                <Button
                  variant="primary"
                  onClick={handleUpdatePatient}
                  disabled={
                    modalLoading ||
                    !formData.firstName ||
                    !formData.phone ||
                    !formData.assignedDoctor
                  }
                >
                  {modalLoading ? 'Saving...' : 'Save Changes'}
                </Button>
              </ButtonGroup>
            </>
          )}

          {modalType === 'deactivate-doctor' && (
            <>
              <ModalTitle>Deactivate Doctor</ModalTitle>
              <p style={{ color: '#4b5563', marginBottom: '1.5rem' }}>
                <strong>{selectedDoctorData?.fullName || selectedDoctorData?.doctorId}</strong> currently has{' '}
                <strong>{formData.patientCount || 0}</strong> assigned patient{(formData.patientCount || 0) === 1 ? '' : 's'}.
                Choose how you would like to reassign these patients before deactivating the doctor.
              </p>

              <FormGroup>
                <Label>Reassignment Strategy *</Label>
                <RadioGroup>
                  <RadioOptionRow selected={formData.strategy === 'equal'}>
                    <input
                      type="radio"
                      name="distributionStrategy"
                      value="equal"
                      checked={formData.strategy === 'equal'}
                      onChange={() => setFormData(prev => ({
                        ...prev,
                        strategy: 'equal',
                        targetDoctorIds: [],
                      }))}
                    />
                    <div>
                      <RadioTitle>Equal distribution across all active doctors</RadioTitle>
                      <RadioDescription>
                        Reassign patients in a round-robin manner to every currently active doctor.
                      </RadioDescription>
                    </div>
                  </RadioOptionRow>

                  <RadioOptionRow selected={formData.strategy === 'least'}>
                    <input
                      type="radio"
                      name="distributionStrategy"
                      value="least"
                      checked={formData.strategy === 'least'}
                      onChange={() => setFormData(prev => ({
                        ...prev,
                        strategy: 'least',
                        targetDoctorIds: [],
                      }))}
                    />
                    <div>
                      <RadioTitle>Assign to doctor with the fewest patients</RadioTitle>
                      <RadioDescription>
                        Transfer all patients to the active doctor currently managing the smallest panel.
                      </RadioDescription>
                    </div>
                  </RadioOptionRow>

                  <RadioOptionRow selected={formData.strategy === 'selected'}>
                    <input
                      type="radio"
                      name="distributionStrategy"
                      value="selected"
                      checked={formData.strategy === 'selected'}
                      onChange={() => setFormData(prev => ({
                        ...prev,
                        strategy: 'selected',
                        targetDoctorIds: prev.targetDoctorIds || [],
                      }))}
                    />
                    <div>
                      <RadioTitle>Manually select doctors</RadioTitle>
                      <RadioDescription>
                        Choose specific active doctors to receive the reassigned patients.
                      </RadioDescription>
                    </div>
                  </RadioOptionRow>
                </RadioGroup>
              </FormGroup>

              {formData.strategy === 'selected' && (
                <FormGroup>
                  <Label>Select active doctors *</Label>
                  <CheckboxList>
                    {doctors.filter(doc => doc.isActive && doc._id !== selectedDoctor).length === 0 ? (
                      <div style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                        No other active doctors are available. Activate another doctor before proceeding.
                      </div>
                    ) : (
                      doctors
                        .filter(doc => doc.isActive && doc._id !== selectedDoctor)
                        .map(doc => (
                          <CheckboxOption key={doc._id}>
                            <input
                              type="checkbox"
                              checked={formData.targetDoctorIds?.includes(doc._id)}
                              onChange={() => toggleTargetDoctorSelection(doc._id)}
                            />
                            <CheckboxText>
                              <span>{doc.doctorId} — {doc.fullName}</span>
                              <CheckboxMeta>{doc.patientCount || 0} patient{(doc.patientCount || 0) === 1 ? '' : 's'}</CheckboxMeta>
                            </CheckboxText>
                          </CheckboxOption>
                        ))
                    )}
                  </CheckboxList>
                </FormGroup>
              )}

              <ButtonGroup>
                <Button onClick={closeModal} disabled={modalLoading}>Cancel</Button>
                <Button
                  variant="primary"
                  onClick={handleDeactivateDoctor}
                  disabled={
                    modalLoading ||
                    (formData.strategy === 'selected' &&
                      (!formData.targetDoctorIds || formData.targetDoctorIds.length === 0))
                  }
                >
                  {modalLoading ? 'Processing...' : 'Reassign & Deactivate'}
                </Button>
              </ButtonGroup>
            </>
          )}

          {modalType === 'add-doctor' && (
            <>
              <ModalTitle>Add New Doctor</ModalTitle>
              <FormGroup>
                <Label>First Name *</Label>
                <Input
                  value={formData.firstName || ''}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  placeholder="Enter first name"
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label>Last Name (Optional)</Label>
                <Input
                  value={formData.lastName || ''}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                  placeholder="Enter last name (optional)"
                />
              </FormGroup>
              <FormGroup>
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={formData.email || ''}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="Enter email address"
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label>Phone Number *</Label>
                <Input
                  type="tel"
                  value={formData.phone || ''}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="Enter phone number"
                  required
                />
              </FormGroup>
              <p style={{ color: '#64748B' }}>The doctor signs in with a hospital Doctor ID. The first password is 123456789, and they choose a new one after signing in.</p>
              <ButtonGroup>
                <Button onClick={closeModal} disabled={modalLoading}>Cancel</Button>
                <Button
                  variant="primary"
                  onClick={handleAddDoctor}
                  disabled={
                    modalLoading ||
                    !formData.firstName ||
                    !formData.email ||
                    !formData.phone
                  }
                >
                  {modalLoading ? 'Adding...' : 'Add Doctor'}
                </Button>
              </ButtonGroup>
            </>
          )}

          {modalType === 'edit-doctor' && (
            <>
              <ModalTitle>Edit Doctor</ModalTitle>
              <FormGroup>
                <Label>First Name</Label>
                <Input
                  value={formData.firstName || ''}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                />
              </FormGroup>
              <FormGroup>
                <Label>Last Name</Label>
                <Input
                  value={formData.lastName || ''}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                />
              </FormGroup>
              <FormGroup>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={formData.email || ''}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </FormGroup>
              <FormGroup>
                <Label>Phone</Label>
                <Input
                  value={formData.phone || ''}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
              </FormGroup>
              <FormGroup>
                <Label>
                  <input
                    type="checkbox"
                    checked={formData.isActive !== undefined ? formData.isActive : true}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    style={{ marginRight: '0.5rem' }}
                  />
                  Active
                </Label>
              </FormGroup>
              <ButtonGroup>
                <Button onClick={closeModal}>Cancel</Button>
                <Button variant="primary" onClick={handleUpdateDoctor}>
                  Update Doctor
                </Button>
              </ButtonGroup>
            </>
          )}
        </ModalContent>
      </Modal>
    );
  };

  const validateFrontDeskForm = (form) => {
    const errors = {};
    if (!form.firstName.trim()) errors.firstName = 'First name is required';
    else if (!/^[A-Za-z][A-Za-z .'-]*$/.test(form.firstName.trim())) errors.firstName = 'Use letters only';
    if (form.lastName.trim() && !/^[A-Za-z][A-Za-z .'-]*$/.test(form.lastName.trim())) errors.lastName = 'Use letters only';
    if (!form.email.trim()) errors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) errors.email = 'Enter a valid email address';
    if (!form.phone.trim()) errors.phone = 'Phone number is required';
    else if (!/^[6-9]\d{9}$/.test(form.phone.trim())) errors.phone = 'Enter a 10-digit mobile number';
    return errors;
  };

  const updateFrontDeskField = (field, value) => {
    setFrontDeskForm((prev) => ({ ...prev, [field]: value }));
    setFrontDeskFieldErrors((prev) => (prev[field] ? { ...prev, [field]: '' } : prev));
  };

  const renderFrontDesk = () => {
    const createFrontDesk = async (event) => {
      event.preventDefault();
      setFrontDeskError('');
      const errors = validateFrontDeskForm(frontDeskForm);
      setFrontDeskFieldErrors(errors);
      if (Object.values(errors).some(Boolean)) return;
      setFrontDeskSaving(true);
      setFrontDeskResult(null);
      try {
        const response = await axios.post('/api/admin/front-desk', {
          firstName: frontDeskForm.firstName.trim(),
          lastName: frontDeskForm.lastName.trim(),
          email: frontDeskForm.email.trim().toLowerCase(),
          phone: frontDeskForm.phone.trim()
        });
        const created = response.data.frontDesk;
        setFrontDeskResult({
          frontDeskId: created.frontDeskId,
          fullName: created.fullName || `${created.firstName} ${created.lastName || ''}`.trim(),
          email: created.email,
          phone: created.phone,
          isActive: created.isActive !== false,
          password: response.data.temporaryPassword
        });
        setFrontDeskForm({ firstName: '', lastName: '', email: '', phone: '' });
        loadFrontDeskStaff().catch(() => {});
      } catch (error) {
        const serverFieldErrors = {};
        (error.response?.data?.errors || []).forEach((item) => {
          const field = item.path || item.param;
          if (field && !serverFieldErrors[field]) serverFieldErrors[field] = item.msg;
        });
        if (Object.keys(serverFieldErrors).length) setFrontDeskFieldErrors(serverFieldErrors);
        setFrontDeskError(error.response?.data?.message || 'Could not create the front desk login');
      } finally {
        setFrontDeskSaving(false);
      }
    };

    const fieldError = (field) => frontDeskFieldErrors[field]
      ? <FormHelperText style={{ color: '#dc2626' }}>{frontDeskFieldErrors[field]}</FormHelperText>
      : null;
    const errorBorder = (field) => (frontDeskFieldErrors[field] ? { borderColor: '#dc2626' } : undefined);

    return (
      <>
      <TabContent>
        <SectionHeader>
          <div>
            <SectionTitle style={{ margin: 0 }}>Create front desk login</SectionTitle>
            <FormHelperText>Reception staff use this login to register patients for your hospital.</FormHelperText>
          </div>
        </SectionHeader>
        <form onSubmit={createFrontDesk} noValidate>
          <FormGrid>
            <FormGroup style={{ marginBottom: 0 }}>
              <Label htmlFor="fd-first-name">First name *</Label>
              <Input id="fd-first-name" autoComplete="off" placeholder="e.g. Priya" value={frontDeskForm.firstName} onChange={(e) => updateFrontDeskField('firstName', e.target.value)} style={errorBorder('firstName')} />
              {fieldError('firstName')}
            </FormGroup>
            <FormGroup style={{ marginBottom: 0 }}>
              <Label htmlFor="fd-last-name">Last name</Label>
              <Input id="fd-last-name" autoComplete="off" placeholder="e.g. Sharma" value={frontDeskForm.lastName} onChange={(e) => updateFrontDeskField('lastName', e.target.value)} style={errorBorder('lastName')} />
              {fieldError('lastName')}
            </FormGroup>
            <FormGroup style={{ marginBottom: 0 }}>
              <Label htmlFor="fd-email">Email *</Label>
              <Input id="fd-email" type="email" autoComplete="off" placeholder="name@hospital.com" value={frontDeskForm.email} onChange={(e) => updateFrontDeskField('email', e.target.value)} style={errorBorder('email')} />
              {fieldError('email')}
            </FormGroup>
            <FormGroup style={{ marginBottom: 0 }}>
              <Label htmlFor="fd-phone">Mobile number *</Label>
              <Input id="fd-phone" type="tel" inputMode="numeric" maxLength={10} autoComplete="off" placeholder="10-digit mobile number" value={frontDeskForm.phone} onChange={(e) => updateFrontDeskField('phone', e.target.value.replace(/\D/g, ''))} style={errorBorder('phone')} />
              {fieldError('phone')}
            </FormGroup>
          </FormGrid>
          <div style={{ marginTop: '1.25rem', padding: '0.875rem 1rem', borderRadius: 10, background: '#eff6ff', color: '#1e3a8a', fontSize: '0.875rem' }}>
            A Front Desk ID is created automatically. The first password is 123456789 and the staff member sets a new one at first sign-in.
          </div>
          {frontDeskError && <p style={{ color: '#dc2626', margin: '1rem 0 0' }}>{frontDeskError}</p>}
          <ButtonRow style={{ marginTop: '1.25rem' }}>
            <Button type="submit" variant="primary" disabled={frontDeskSaving}>{frontDeskSaving ? 'Creating...' : 'Create front desk login'}</Button>
            <Button
              type="button"
              variant="secondary"
              disabled={frontDeskSaving}
              onClick={() => { setFrontDeskForm({ firstName: '', lastName: '', email: '', phone: '' }); setFrontDeskFieldErrors({}); setFrontDeskError(''); }}
            >
              Clear
            </Button>
          </ButtonRow>
        </form>
        {frontDeskResult && (
          <div role="status" style={{ marginTop: '1.5rem', border: '1px solid #bbf7d0', borderRadius: 12, overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', padding: '0.875rem 1.25rem', background: '#f0fdf4', borderBottom: '1px solid #bbf7d0' }}>
              <strong style={{ color: '#166534' }}>Front desk account created</strong>
              <Badge variant={frontDeskResult.isActive ? 'success' : 'danger'}>{frontDeskResult.isActive ? 'Active' : 'Inactive'}</Badge>
            </div>
            <div style={{ padding: '1.25rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem' }}>
              {[
                ['Name', frontDeskResult.fullName],
                ['Front Desk ID', frontDeskResult.frontDeskId, true],
                ['Email', frontDeskResult.email, true],
                ['Phone', frontDeskResult.phone],
                ['Temporary password', frontDeskResult.password, true]
              ].map(([label, value, copyable]) => (
                <div key={label}>
                  <div style={{ fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: 4 }}>
                    <strong style={{ color: '#1e293b', wordBreak: 'break-all', fontFamily: label === 'Front Desk ID' || label === 'Temporary password' ? 'monospace' : undefined }}>{value || '—'}</strong>
                    {copyable && value && (
                      <button
                        type="button"
                        onClick={() => navigator.clipboard?.writeText(value).then(() => toast.success(`${label} copied`)).catch(() => toast.error('Could not copy'))}
                        style={{ border: '1px solid #cbd5e1', background: '#fff', borderRadius: 6, padding: '2px 8px', fontSize: '0.75rem', color: '#0b63c5', cursor: 'pointer' }}
                      >
                        Copy
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div style={{ padding: '0.875rem 1.25rem', background: '#fffbeb', borderTop: '1px solid #fde68a', color: '#92400e', fontSize: '0.875rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
              <span>The temporary password is shown only once. Share it now; the staff member signs in at /front-desk and must set a new password.</span>
              <Button type="button" variant="primary" onClick={() => setFrontDeskResult(null)}>Done</Button>
            </div>
          </div>
        )}
      </TabContent>

      <TabContent style={{ marginTop: '1.5rem' }}>
        <SectionHeader>
          <div>
            <SectionTitle style={{ margin: 0 }}>Front desk accounts</SectionTitle>
            <FormHelperText>
              {frontDeskStaff.length} account{frontDeskStaff.length === 1 ? '' : 's'} · {frontDeskStaff.filter((staff) => staff.isActive).length} active
            </FormHelperText>
          </div>
        </SectionHeader>
        {frontDeskStaff.length === 0 ? (
          <EmptyState>No front desk accounts yet. Create one above.</EmptyState>
        ) : (
          <TableContainer>
            <Table>
              <TableHeader>
                <tr>
                  <TableHeaderCell>Name</TableHeaderCell>
                  <TableHeaderCell>Front Desk ID</TableHeaderCell>
                  <TableHeaderCell>Email</TableHeaderCell>
                  <TableHeaderCell>Phone</TableHeaderCell>
                  <TableHeaderCell>Status</TableHeaderCell>
                  <TableHeaderCell>Created</TableHeaderCell>
                  <TableHeaderCell>Actions</TableHeaderCell>
                </tr>
              </TableHeader>
              <tbody>
                {frontDeskStaff.map((staff) => (
                  <TableRow key={staff._id}>
                    <TableCell>{staff.fullName || '-'}</TableCell>
                    <TableCell style={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{staff.frontDeskId || '-'}</TableCell>
                    <TableCell>{staff.email || '-'}</TableCell>
                    <TableCell>{staff.phone || '-'}</TableCell>
                    <TableCell>
                      <Badge variant={staff.isActive ? 'success' : 'danger'}>{staff.isActive ? 'Active' : 'Inactive'}</Badge>
                      {staff.mustChangePassword && (
                        <div style={{ fontSize: '0.75rem', color: '#d97706', marginTop: 4 }}>First sign-in pending</div>
                      )}
                    </TableCell>
                    <TableCell style={{ whiteSpace: 'nowrap' }}>{staff.createdAt ? format(new Date(staff.createdAt), 'MMM dd, yyyy') : '-'}</TableCell>
                    <TableCell>
                      <Button
                        type="button"
                        variant={staff.isActive ? 'danger' : 'success'}
                        disabled={frontDeskStatusBusy === staff._id}
                        onClick={() => toggleFrontDeskStatus(staff)}
                      >
                        {frontDeskStatusBusy === staff._id ? 'Saving...' : staff.isActive ? 'Deactivate' : 'Activate'}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </tbody>
            </Table>
          </TableContainer>
        )}
      </TabContent>
      </>
    );
  };

  return (
    <DashboardContainer>
      <Sidebar>
        <SidebarHeader>
          <img src="/logo-wordmark.jpg" alt="Anuvedana" style={{ display: 'block', width: 112, height: 112, margin: '0 auto', borderRadius: 12 }} />
          <HospitalLogo logoUrl={user?.hospitalLogoUrl} name={user?.hospitalName} size={72} stacked style={{ marginTop: '0.6rem' }} />
          {(user?.hospitalPublicId || user?.hospitalCode) && (
            <div style={{ marginTop: '0.5rem', textAlign: 'center', fontSize: '0.75rem', color: 'rgba(255, 255, 255, 0.7)', fontFamily: 'monospace', lineHeight: 1.5 }}>
              {user?.hospitalPublicId && <div>Hospital ID: {user.hospitalPublicId}</div>}
              {user?.hospitalCode && <div>Code: {user.hospitalCode}</div>}
            </div>
          )}
        </SidebarHeader>

        <NavMenu>
          <NavItem active={activeTab === 'overview'} onClick={() => setActiveTab('overview')}>
            <NavIcon>📊</NavIcon>
            Overview
          </NavItem>
          {canSeeAnalytics && (
            <NavItem active={activeTab === 'analytics'} onClick={() => setActiveTab('analytics')}>
              <NavIcon>📈</NavIcon>
              Analytics
            </NavItem>
          )}
          <NavItem active={activeTab === 'patients'} onClick={() => setActiveTab('patients')}>
            <NavIcon>👥</NavIcon>
            Patients
          </NavItem>
          <NavItem active={activeTab === 'doctors'} onClick={() => setActiveTab('doctors')}>
            <NavIcon>👨‍⚕️</NavIcon>
            Doctors
          </NavItem>
          <NavItem active={activeTab === 'front-desk'} onClick={() => setActiveTab('front-desk')}>
            <NavIcon>🗂️</NavIcon>
            Front Desk
          </NavItem>
          <NavItem active={activeTab === 'accounts'} onClick={() => setActiveTab('accounts')}>
            <NavIcon>🔒</NavIcon>
            Accounts
          </NavItem>
        </NavMenu>
      </Sidebar>

      <MainContent>
        <AdminTopBar
          title={TAB_TITLES[activeTab] || 'Dashboard'}
          user={user}
          onOpenProfile={() => setActiveTab('profile')}
          onLogout={handleLogout}
        />
        {activeTab === 'overview' && renderOverview()}
        {activeTab === 'analytics' && canSeeAnalytics && renderAnalytics()}
        {activeTab === 'profile' && renderProfile()}
        {activeTab === 'patients' && renderPatients()}
        {activeTab === 'doctors' && renderDoctors()}
        {activeTab === 'front-desk' && renderFrontDesk()}
        {activeTab === 'accounts' && renderAccounts()}
      </MainContent>

      {renderModal()}
    </DashboardContainer>
  );
};

export default AdminDashboard;
